import React from 'react';
import { useRecoilValue } from 'recoil';

import { CarrierLogo, CarrierLogoVariants } from '@/components/modules/carrier/logo';
import { Tooltip } from '@/components/tooltip';
import { getCarriersByIdsState } from '@/state/carriers';
import { SegmentModeType } from '@/types/api-types';
import { getCarrierName } from '@/utils/carrier';
import { tv } from '@/utils/styles';

export interface CarrierLogoStackProps {
  variant?: CarrierLogoVariants;
  carrierIds: string[];
  limit?: number;
  mode?: SegmentModeType;
}

const CarrierLogoStack = ({ variant = 'sm', carrierIds, limit = 5, mode }: CarrierLogoStackProps) => {
  const carriers = useRecoilValue(getCarriersByIdsState(carrierIds));
  const limitedCarriers = carriers.slice(0, limit);
  const carrierCount = carrierIds.length;
  const visibleCarriersCount = carrierCount > limit ? limit : carrierCount;
  const remainingCarrierCount = carrierCount - limitedCarriers.length;

  const { circle } = styles({ variant });

  return (
    <span className="flex">
      {limitedCarriers.map((carrier, i) => {
        const carrierName = getCarrierName(carrier);
        const hoverIndex = visibleCarriersCount - (i + 1);

        return (
          <Tooltip key={`${i}${carrier.identifiers[0]?.value}`} content={carrierName}>
            <div className={circle()} style={{ zIndex: hoverIndex }}>
              <CarrierLogo carrier={carrier} mode={mode} variant={variant} />
            </div>
          </Tooltip>
        );
      })}
      <Tooltip content={`+ ${remainingCarrierCount} more`}>
        {remainingCarrierCount > 0 && <div className={circle()}>+{remainingCarrierCount}</div>}
      </Tooltip>
    </span>
  );
};

const styles = tv({
  slots: {
    circle:
      'grid cursor-pointer place-items-center overflow-hidden rounded-3xl bg-white text-grey-500 shadow-[0_0_1px_rgba(0,0,0,0.5)] first:!ml-0 hover:!z-5',
  },
  variants: {
    variant: {
      sm: { circle: '-ml-[0.4rem] h-5 w-5 text-[10px]' },
      md: { circle: '-ml-[0.9rem] h-9 w-9 text-sm' },
    },
  },
});

export { CarrierLogoStack };
